import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { get } from 'lodash'
import { Mail, Phone, X } from 'lucide-react'
import { formatPhoneNumber } from 'react-phone-number-input'

import { Button } from '../../../ui/button'
import { Input } from '../../../ui/input'
import { Label } from '../../../ui/label'
import { Card, CardContent } from '../../../ui/card'
import { Badge } from '../../../ui/badge'
import LoadingSpinner from '../../../leevo_ui/LoadingSpinner/LoadingSpinner'
import { requestCreator } from '../../../../utilities/requests'

const { post: addGuardian, delete: removeGuardian, cancel: cancelGuardianRequests } = requestCreator()

function GuardianManager({ className, guardians = [], studentId, onChange, ...rest }) {
  const [guardianList, setGuardianList] = useState(guardians)
  const [removingId, setRemovingId] = useState(null)
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      newGuardianEmail: ''
    }
  })

  useEffect(() => {
    return cancelGuardianRequests
  }, [])

  function handleError(error) {
    const message = get(error, 'response.data.error') || 'Something went wrong.'
    history.replace({
      state: { notification: { message, color: 'red' } }
    })
  }

  async function onSubmit(data) {
    try {
      let promise = Promise.resolve({ email: data.newGuardianEmail })

      if (studentId) {
        promise = addGuardian(`/api/students/${studentId}/guardians`, {
          guardian: { email: data.newGuardianEmail }
        })
      }

      const newGuardian = await promise
      const updatedList = [...guardianList, newGuardian]
      setGuardianList(updatedList)
      onChange(updatedList)
      reset()
    } catch (error) {
      handleError(error)
    }
  }

  async function handleRemove(id) {
    setRemovingId(id)
    const url = `/api/students/${studentId}/guardians/${id}`

    try {
      if (id) await removeGuardian(url)
      const updatedList = guardianList.filter((guardian) => guardian.id != id)
      setGuardianList(updatedList)
      onChange(updatedList)
    } catch (error) {
      handleError(error)
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <div {...rest} className={className}>
      <form onSubmit={handleSubmit(onSubmit)} className="mb-4">
        <Label htmlFor="newGuardianEmail" className="text-base font-medium">
          Enter the email of the new guardian
        </Label>
        <p className="text-xs text-gray-500 mb-2">If they don&apos;t have an account, they will get an invite.</p>
        <div className="flex gap-2">
          <Input
            id="newGuardianEmail"
            type="email"
            placeholder="Enter the guardian's email address."
            {...register('newGuardianEmail', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            className={errors.newGuardianEmail ? 'border-red-500' : ''}
          />
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add'}
          </Button>
        </div>
        {errors.newGuardianEmail && <p className="mt-1 text-xs text-red-500">{errors.newGuardianEmail.message}</p>}
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {guardianList.map((guardian, index) => {
          const showBadge = guardian.pending || guardian.emailBounced
          const badgeVariant = guardian.emailBounced ? 'destructive' : 'secondary'
          const badgeText = guardian.emailBounced ? 'Emails Bounced' : 'Pending Invite'
          const isRemoving = removingId === guardian.id

          return (
            <Card key={guardian.id || guardian.email || `temp-${index}`}>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {guardian.fullName && (
                    <div className="flex items-center justify-between">
                      <p className="text-base font-semibold text-gray-900">{guardian.fullName}</p>
                      {showBadge && <Badge variant={badgeVariant}>{badgeText}</Badge>}
                    </div>
                  )}
                  {guardian.email && (
                    <a
                      href={`mailto:${guardian.email}`}
                      className="flex items-center text-sm text-purple-600 hover:text-purple-700"
                    >
                      <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{guardian.email}</span>
                      {showBadge && !guardian.fullName && (
                        <Badge variant={badgeVariant} className="ml-2">
                          {badgeText}
                        </Badge>
                      )}
                    </a>
                  )}
                  {guardian.phone && (
                    <a
                      href={`tel:${guardian.phone}`}
                      className="flex items-center text-sm text-purple-600 hover:text-purple-700"
                    >
                      <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                      {formatPhoneNumber(guardian.phone)}
                    </a>
                  )}
                  <div className="flex justify-end pt-2 border-t border-gray-100">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(guardian.id)}
                      disabled={isRemoving}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      {isRemoving ? (
                        <LoadingSpinner color="red" className="w-4 h-4 mr-1" />
                      ) : (
                        <X className="w-4 h-4 mr-1" />
                      )}
                      Remove
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

GuardianManager.propTypes = {
  guardians: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      fullName: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
      cloudinaryPhotoPublicId: PropTypes.string,
      pending: PropTypes.bool,
      emailBounced: PropTypes.bool
    })
  ),
  studentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  className: PropTypes.string
}

export default GuardianManager
